import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import pwaService from '@/services/pwaService';
import pushNotificationService from '@/services/pushNotificationService';

const DISMISS_KEY = 'pushPromptDismissedAt';
const SNOOZE_DAYS = 14;
const SNOOZE_MS = SNOOZE_DAYS * 24 * 60 * 60 * 1000;

/**
 * PushPermissionPrompt — role-agnostic modal to request browser push permission.
 *
 * Shown once per user on first dashboard load. Dismissal is snoozed for 14 days.
 * If the user has already granted permission (and is subscribed), renders nothing.
 * If the browser doesn't support push, renders nothing.
 */
const PushPermissionPrompt = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Don't show if browser doesn't support push at all
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

    const check = async () => {
      const permission = Notification.permission;

      // Already denied — browser-level block; nothing we can do in UI
      if (permission === 'denied') return;

      // Already granted — silently ensure we're subscribed if not already
      if (permission === 'granted') {
        const alreadySubscribed = await pwaService.isPushSubscribed();
        if (!alreadySubscribed) {
          // Permission granted but no subscription — re-subscribe silently
          try {
            const { vapid_public_key } = await pushNotificationService.getVapidPublicKey();
            const sub = await pwaService.subscribeToPush(vapid_public_key);
            await pushNotificationService.registerSubscription(sub);
          } catch {
            // Non-fatal — will retry next load
          }
        }
        return;
      }

      // permission === 'default' — check snooze
      const dismissedAt = localStorage.getItem(DISMISS_KEY);
      if (dismissedAt && Date.now() - Number(dismissedAt) < SNOOZE_MS) return;

      setOpen(true);
    };

    check();
  }, []);

  const handleAccept = async () => {
    setOpen(false);
    try {
      const result = await Notification.requestPermission();
      if (result !== 'granted') return;

      const { vapid_public_key } = await pushNotificationService.getVapidPublicKey();
      const sub = await pwaService.subscribeToPush(vapid_public_key);
      await pushNotificationService.registerSubscription(sub);
    } catch (err) {
      console.error('[PushPrompt] Subscription failed:', err);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setOpen(false);
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={handleDismiss} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <NotificationsActiveIcon color="primary" />
        Browser notifications
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          Application notifications can be turned off in your browser anytime.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleDismiss} color="inherit" size="small">
          Remind me later
        </Button>
        <Button onClick={handleAccept} variant="contained" size="small">
          Allow notifications
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PushPermissionPrompt;
